import { Logo, MenuOpen } from '@/components/icons'
import { ProfileMenu } from '@/components/profile-menu'
import { isProd } from '@/constants/Common'
import { ROUTE_LOGIN, ROUTE_MAIN, ROUTE_SIGNUP } from '@/constants/Routes'
import { useOutsideClick } from '@/hooks/useOutsideClick'
import { useAppDispatch, useAppSelector } from '@/hooks/useRedux'
import { authSelector } from '@/pages/auth/selectors'
import { setShowDropDown, setShowMenu } from '@/pages/home/appSlice'
import { appSelector } from '@/pages/home/selectors'
import { userSelector } from '@/pages/user/selectors'
import { ReactNode, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'
import { Button } from '../button'
import { DropdownProfile } from '../dropdown/Profile'
import { IFrameMobileModal, IFrameModal } from '../modal'

export const MainLayout = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch()
  const { token } = useAppSelector(authSelector)
  const { isShowMenu } = useAppSelector(appSelector)
  const { user } = useAppSelector(userSelector)
  const isMobile = useMediaQuery('(max-width: 767px)')

  const [showDropdownProfileMenu, setShowDropdownProfileMenu] = useState(false)
  const [showDropdownMessage, setShowDropdownMessage] = useState(false)
  const [openIframe, setOpenIframe] = useState(false)

  const onToggleIframe = () => setOpenIframe(!openIframe)

  const handleClickOutsideDropDown = () => {
    if (!isMobile) setShowDropdownProfileMenu(false)
  }

  const profileMenuRef = useOutsideClick(handleClickOutsideDropDown)

  const toggleShowDropdownProfileMenu = () => {
    setShowDropdownProfileMenu(!showDropdownProfileMenu)
    setShowDropdownMessage(false)
  }

  const onShowProfileMenu = () => {
    toggleShowDropdownProfileMenu()
    dispatch(setShowDropDown('PROFILE'))
  }

  const onCloseProfileMenu = () => {
    toggleShowDropdownProfileMenu()
    dispatch(setShowDropDown(''))
  }

  const toggleMenu = () => {
    dispatch(setShowMenu(!isShowMenu))
  }

  useEffect(() => {
    if (showDropdownProfileMenu || showDropdownMessage) {
      document.body.style.overflowY = 'hidden'
    }
    if (!showDropdownProfileMenu && !showDropdownMessage) {
      document.body.style.overflowY = 'auto'
    }
  }, [showDropdownProfileMenu, showDropdownMessage])

  let lastScrollTop = 0
  const header = document.getElementById('header')
  const content = document.querySelector('.content')
  const statistical = document.querySelector('.statistical')

  window.addEventListener(
    'scroll',
    function () {
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop

      if (header && content)
        if (currentScroll > lastScrollTop) {
          header.classList.remove('header-hidden')
          header.classList.add('header-visible')
          content.classList.add('content-with-header')
          content.classList.remove('content-without-header')
          if (statistical) {
            statistical.classList.add('statistical-with-header')
            statistical.classList.remove('statistical-without-header')
          }
        } else {
          if (currentScroll < 50) {
            header.classList.add('header-visible')
            header.classList.remove('header-hidden')
            content.classList.add('content-with-header')
            content.classList.remove('content-without-header')
            if (statistical) {
              statistical.classList.add('statistical-with-header')
              statistical.classList.remove('statistical-without-header')
            }
          } else {
            header.classList.add('header-hidden')
            header.classList.remove('header-visible')
            content.classList.add('content-without-header')
            content.classList.remove('content-with-header')
            if (statistical) {
              statistical.classList.add('statistical-without-header')
              statistical.classList.remove('statistical-with-header')
            }
          }
        }

      lastScrollTop = currentScroll <= 0 ? 0 : currentScroll
    },
    false
  )

  return (
    <>
      <div className="mx-4 lg:mx-6 xl:mx-9">
        <div
          id="header"
          className="fixed left-4 right-4 top-12 sm:top-4 lg:left-6 lg:right-6 xl:left-9 xl:right-9 lg:top-12 rounded-full border border-white border-opacity-10 h-14 shadow-lg bg-white backdrop-blur-sm flex justify-between items-center z-20 header-visible"
        >
          <div className="flex items-center ml-7 gap-x-4">
            <Button onClick={toggleMenu} className="hidden lg:inline hover:opacity-50">
              <MenuOpen className="text-navy-900" />
            </Button>
            <Link to={`${isProd ? 'https://www.dupr.com' : ROUTE_MAIN}?r=0`}>
              <Logo />
            </Link>
          </div>
          <div className="flex gap-x-2 mr-4">
            {token && user ? (
              <>
                {/* Chat is temporarily disabled: the header message entry point has been removed. */}
                <div ref={profileMenuRef}>
                  <ProfileMenu
                    imgClass="w-9 h-9"
                    toggleShowProfileMenu={onShowProfileMenu}
                    toggleShowDropdown={toggleShowDropdownProfileMenu}
                  />

                  {/* Dropdown profile menu */}
                  <DropdownProfile
                    isDropdown
                    openDropdown={showDropdownProfileMenu}
                    user={user}
                    dropdownClass="fixed top-16 right-0"
                    onCloseModal={onCloseProfileMenu}
                    onRunEvent={() => {
                      setShowDropdownProfileMenu(false)
                      onToggleIframe()
                    }}
                  />
                </div>
              </>
            ) : (
              <>
                <Link
                  to={ROUTE_LOGIN}
                  className="bg-white rounded px-2 py-1 text-sm font-medium text-darkNavy hover:bg-slate-200"
                >
                  Log In
                </Link>
                <Link
                  to={ROUTE_SIGNUP}
                  className="bg-azul text-white text-sm px-2 py-1 rounded hover:bg-primary font-medium"
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
        <div className="content content-with-header pt-36 sm:pt-28 lg:pt-36">{children}</div>
      </div>

      {/* Modal profile menu */}
      {showDropdownProfileMenu && user && (
        <DropdownProfile
          user={user}
          onCloseModal={onCloseProfileMenu}
          onRunEvent={() => {
            setShowDropdownProfileMenu(false)
            onToggleIframe()
          }}
        />
      )}

      {isMobile ? (
        <IFrameMobileModal open={openIframe} onClose={onToggleIframe} />
      ) : (
        <IFrameModal open={openIframe} onClose={onToggleIframe} />
      )}
    </>
  )
}
